import {ChangeEvent, Dispatch, SetStateAction, SyntheticEvent} from "react";
import {SelectChangeEvent} from "@mui/material";
import {replaceElementByIndex} from "./ArrayUtil";

export type SetState<T> = Dispatch<SetStateAction<T>>;

export function buildSetState<Value>(
    body: (newValue: (prevValue: Value) => Value) => void,
): SetState<Value> {
    return (valueOrFunction: SetStateAction<Value>) => {
        body(prevValue => {
            if (typeof valueOrFunction === 'function') {
                return (valueOrFunction as (prevValue: Value) => Value)(prevValue);
            } else {
                return valueOrFunction;
            }
        });
    };
}

export function mapSetState<Form extends {}, FieldName extends keyof Form>(setForm: SetState<Form>, fieldName: FieldName): SetState<Form[FieldName]> {
    return buildSetState(newValue => {
        setForm(prevForm => ({
            ...prevForm,
            [fieldName]: newValue(prevForm[fieldName]),
        }));
    });
}

export function mapArraySetState<E>(setForm: SetState<E[]>, index: number): SetState<E> {
    return buildSetState(newValue => {
        setForm(prevForm => replaceElementByIndex(prevForm, index, newValue(prevForm[index])));
    });
}

export function patch<Form>(setForm: SetState<Form>, patch: (prevForm: Form) => Partial<Form> | undefined) {
    setForm(prevForm => ({...prevForm, ...patch(prevForm)}));
}

export function formPatcher<Value, Form>(setForm: SetState<Form>, handleValue: (value: Value, prevForm: Form) => Partial<Form>): (value: Value) => void {
    return (value: Value) => {
        patch(setForm, prevForm => handleValue(value, prevForm));
    };
}

export function autocompleteChange<Value, Form>(
    setForm: SetState<Form>,
    handleValue: (value: Value) => Partial<Form>,
): (event: SyntheticEvent, value: Value) => void {
    return autocompleteChangeEvent(formPatcher(setForm, handleValue));
}

export function selectChange<Value, Form>(
    setForm: SetState<Form>,
    handleValue: (value: Value | string) => Partial<Form>,
): (event: SelectChangeEvent<Value>) => void {
    return selectChangeEvent(formPatcher(setForm, handleValue));
}

export function switchChange<Form>(
    setForm: SetState<Form>,
    handleValue: (isChecked: boolean) => Partial<Form>,
): (event: any, checked: boolean) => void {
    return switchChangeEvent(formPatcher(setForm, handleValue));
}

export function inputChange<Form>(
    setForm: SetState<Form>,
    handleValue: (value: string) => Partial<Form>,
): (event: ChangeEvent<HTMLInputElement>) => void {
    return inputChangeEvent(formPatcher(setForm, handleValue));
}

export function autocompleteChangeEvent<Value>(
    handleValue: (value: Value) => void,
): (event: SyntheticEvent, value: Value) => void {
    return (_event, newValue) => {
        handleValue(newValue);
    };
}


export function selectChangeEvent<Value>(
    handleValue: (value: Value | string) => void,
): (event: SelectChangeEvent<Value>) => void {
    return (event: SelectChangeEvent<Value>) => {
        handleValue(event.target.value);
    };
}

export function switchChangeEvent(
    handleValue: (isChecked: boolean) => void,
): (event: any, checked: boolean) => void {
    return (_event: any, checked: boolean) => {
        handleValue(checked);
    };
}


export function inputChangeEvent(handleValue: (newValue: string) => void) {
    return (event: ChangeEvent<HTMLInputElement>) => {
        handleValue(event.target.value);
    };
}

export function toggleButtonEvent(handleValue: (newValue: any) => void) {
    return (event: React.MouseEvent<HTMLElement>, value: any) => {
        handleValue(value);
    };
}


